import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Shuffle as ShuffleIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import LotteryBall from '../components/LotteryBall';
import SelectedPreview from '../components/LotterySelectedPreview';
import { randomPickSSQ, calculateCountdown } from '../utils/lotteryUtils';
import { getCurrentPeriod, submitLotteryOrder, saveOrderToLocal } from '../services/lotteryApi';
import Navigation from '../components/Navigation';

/**
 * 双色球彩票领取（选号）页面
 * 移动端优先设计
 */
function LotteryPickPage() {
  const history = useHistory();
  const [redBalls, setRedBalls] = useState([]);
  const [blueBall, setBlueBall] = useState('');
  const [periodInfo, setPeriodInfo] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [error, setError] = useState('');

  // 红球池 01-33
  const redPool = Array.from({ length: 33 }, (_, i) =>
    String(i + 1).padStart(2, '0')
  );

  // 蓝球池 01-16
  const bluePool = Array.from({ length: 16 }, (_, i) =>
    String(i + 1).padStart(2, '0')
  );

  // 加载期号信息
  const loadPeriodInfo = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCurrentPeriod();
      if (result.success) {
        setPeriodInfo(result.data);
      }
    } catch (err) {
      console.error('加载期号信息失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化
  useEffect(() => {
    loadPeriodInfo();
  }, [loadPeriodInfo]);

  // 倒计时更新
  useEffect(() => {
    if (!periodInfo?.endTime) return;

    const timer = setInterval(() => {
      const cd = calculateCountdown(periodInfo.endTime);
      setCountdown(cd);
    }, 1000);

    return () => clearInterval(timer);
  }, [periodInfo]);

  // 选择红球
  const handleRedBallClick = (num) => {
    if (redBalls.includes(num)) {
      // 取消选择
      setRedBalls(redBalls.filter(n => n !== num));
    } else {
      // 最多选6个
      if (redBalls.length < 6) {
        const newRedBalls = [...redBalls, num].sort((a, b) => parseInt(a) - parseInt(b));
        setRedBalls(newRedBalls);
      }
    }
  };

  // 选择蓝球
  const handleBlueBallClick = (num) => {
    setBlueBall(num === blueBall ? '' : num);
  };

  // 机选号码
  const handleRandomPick = () => {
    const pick = randomPickSSQ();
    setRedBalls(pick.redBalls);
    setBlueBall(pick.blueBall);
  };

  // 清空重选
  const handleClear = () => {
    setRedBalls([]);
    setBlueBall('');
  };

  // 是否选号完成
  const isComplete = redBalls.length === 6 && blueBall;

  // 提交订单前确认
  const handleSubmitConfirm = () => {
    if (!isComplete) {
      setError('请选满6个红球和1个蓝球');
      return;
    }
    setConfirmDialogOpen(true);
  };

  // 确认提交订单
  const handleSubmitOrder = async () => {
    try {
      setSubmitLoading(true);
      setConfirmDialogOpen(false);

      const result = await submitLotteryOrder({
        redBalls,
        blueBall,
        period: periodInfo?.period || '2024088',
        betCount: 1,
        multiple: 1
      });

      if (result.success) {
        setLastOrder(result.data);
        setSuccessDialogOpen(true);
        // 清空选择
        handleClear();
      } else {
        setError(result.message || '下单失败，请重试');
      }
    } catch (err) {
      console.error('下单失败:', err);
      setError('网络请求失败，请稍后重试');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 跳转查看我的彩票
  const goToMyLottery = () => {
    setSuccessDialogOpen(false);
    history.push('/lottery/list');
  };

  return (
    <Box>
      {/* 系统导航栏 */}
      <Navigation />
      
      {/* 页面内容 */}
      <Box sx={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', backgroundColor: '#F5F5F5' }}>
      {/* 顶部导航 */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#FFFFFF',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <IconButton onClick={() => history.goBack()} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          双色球领取
        </Typography>
        <IconButton onClick={handleRandomPick} size="small" color="primary">
          <ShuffleIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* 期号信息卡片 */}
        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : periodInfo ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#E53935' }}>
                  第 {periodInfo.period} 期
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  距离截止：
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: countdown.hours === 0 && countdown.minutes < 30 ? '#E53935' : '#1E88E5'
                  }}
                >
                  {String(countdown.hours).padStart(2, '0')}:
                  {String(countdown.minutes).padStart(2, '0')}:
                  {String(countdown.seconds).padStart(2, '0')}
                </Typography>
              </Box>
            </>
          ) : null}
        </Paper>

        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 红球选择区 */}
        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#E53935' }}>
            🎯 选择红球（{redBalls.length}/6）
          </Typography>
          <Grid container spacing={1.5}>
            {redPool.map(num => (
              <Grid item key={num} xs={2}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LotteryBall
                    number={num}
                    type="red"
                    selected={redBalls.includes(num)}
                    onClick={() => handleRedBallClick(num)}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* 蓝球选择区 */}
        <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1E88E5' }}>
            🔵 选择蓝球（{blueBall ? 1 : 0}/1）
          </Typography>
          <Grid container spacing={1.5}>
            {bluePool.map(num => (
              <Grid item key={num} xs={2}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LotteryBall
                    number={num}
                    type="blue"
                    selected={blueBall === num}
                    onClick={() => handleBlueBallClick(num)}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* 快捷操作按钮 */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={handleRandomPick}
            sx={{ py: 1.5 }}
          >
            机选号码
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleClear}
            sx={{ py: 1.5 }}
          >
            清空重选
          </Button>
        </Box>

        {/* 已选号码预览 */}
        <Box sx={{ mb: 2 }}>
          <SelectedPreview redBalls={redBalls} blueBall={blueBall} />
        </Box>

        {/* 底部提交按钮 */}
        <Box sx={{ pb: 4 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isComplete || submitLoading}
            onClick={handleSubmitConfirm}
            sx={{
              py: 2,
              backgroundColor: isComplete ? '#E53935' : '#CCCCCC',
              '&:hover': {
                backgroundColor: isComplete ? '#C62828' : '#CCCCCC'
              },
              fontSize: 18,
              fontWeight: 700,
              borderRadius: 2
            }}
          >
            {submitLoading ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : (
              <CheckIcon sx={{ mr: 1 }} />
            )}
            确认领取彩票
          </Button>
        </Box>
      </Box>

      {/* 确认提交对话框 */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          确认领取彩票
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
              您选择的号码：
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#E53935', mr: 1 }}>
                红球：
              </Typography>
              {redBalls.map(num => (
                <LotteryBall key={num} number={num} type="red" selected={true} size="small" disabled />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#1E88E5', mr: 1 }}>
                蓝球：
              </Typography>
              <LotteryBall number={blueBall} type="blue" selected={true} size="small" disabled />
            </Box>
          </Box>
          <Alert severity="info" sx={{ mb: 1 }}>
            领取1注彩票消耗10积分
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setConfirmDialogOpen(false)}
            startIcon={<CloseIcon />}
          >
            取消
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmitOrder}
            startIcon={<CheckIcon />}
            disabled={submitLoading}
            sx={{ backgroundColor: '#E53935' }}
          >
            确认领取
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功对话框 */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}
            >
              <CheckIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4CAF50' }}>
              🎉 领取成功！
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
              您的彩票订单已提交成功
            </Typography>
            {lastOrder && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', color: '#999' }}>
                订单号：{lastOrder.orderNo}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setSuccessDialogOpen(false)}
          >
            继续选号
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={goToMyLottery}
            sx={{ backgroundColor: '#E53935' }}
          >
            查看我的彩票
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
}

export default LotteryPickPage;
